
import { useState, useCallback } from "react";
import { FormFields } from "./form/FormFields";
import { FormButtons } from "./form/FormButtons";
import { useDeployContract } from "./form/useDeployContract";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { NameScreening } from "./NameScreening";
import { Card } from "@/components/ui/card";
import { useToast } from "@/components/ui/use-toast";
import { useAccount } from "wagmi";
import { Checkbox } from "@/components/ui/checkbox";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { Info } from "lucide-react";
import { LaunchTrustFields } from "@/components/identity/LaunchTrustFields";
import { launchTrustConfigured } from "@/features/identity/useLaunchTrustRegistration";

interface FormData {
  name: string;
  symbol: string;
  initialSupply: string;
  description: string;
  referralCode: string;
  fairLaunch: boolean;
  fairLaunchDuration: string;
  fairLaunchTarget: string;
  trustEnabled: boolean;
  founderEntityId: string;
  entityMetadataURI: string;
  projectMetadataURI: string;
}

const INITIAL_FORM_STATE: FormData = {
  name: "",
  symbol: "",
  initialSupply: "",
  description: "",
  referralCode: "",
  fairLaunch: false,
  fairLaunchDuration: "86400",
  fairLaunchTarget: "20",
  trustEnabled: launchTrustConfigured,
  founderEntityId: "",
  entityMetadataURI: "",
  projectMetadataURI: "",
};

const formatDurationHours = (seconds: string) => {
  const value = Number(seconds);
  if (!Number.isFinite(value) || value <= 0) return "Enter a duration to see the hour conversion.";
  return `≈ ${(value / 3600).toLocaleString(undefined, { maximumFractionDigits: 2 })} hours`;
};

export function CreateCoinForm() {
  const { isDeploying, deployContract } = useDeployContract();
  const [isNameValid, setIsNameValid] = useState(false);
  const [formData, setFormData] = useState<FormData>(INITIAL_FORM_STATE);
  const { isConnected } = useAccount();
  const { toast } = useToast();

  // Optimize form handling with useCallback
  const handleSubmit = useCallback(async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!isConnected) {
      toast({
        title: "Wallet Not Connected",
        description: "Please connect your wallet to create a coin",
        variant: "destructive",
      });
      return;
    }
    
    if (!isNameValid) {
      toast({
        title: "Invalid Name",
        description: "Please ensure the token name passes the screening",
        variant: "destructive",
      });
      return;
    }
    
    if (!formData.symbol) {
      toast({
        title: "Missing Symbol",
        description: "Please enter a token symbol",
        variant: "destructive",
      });
      return;
    }
    
    await deployContract(formData);
  }, [formData, isNameValid, isConnected, deployContract, toast]);

  // Memoize change handler
  const handleChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  }, []);

  // Ensure symbol is uppercase for better UX
  const handleSymbolChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value.toUpperCase();
    setFormData(prev => ({ ...prev, symbol: value }));
  }, []);

  return (
    <Card className="p-6">
      <form onSubmit={handleSubmit} className="space-y-6">
        <FormFields 
          formData={formData} 
          handleChange={handleChange}
          handleSymbolChange={handleSymbolChange}
        />
        
        <NameScreening 
          name={formData.name} 
          onValidationChange={setIsNameValid} 
        />

        <LaunchTrustFields
          value={formData}
          onChange={(trust) => setFormData((previous) => ({ ...previous, ...trust }))}
        />
        
        <div className="space-y-2">
          <Label htmlFor="referralCode">Referral Code (Optional)</Label>
          <Input
            id="referralCode"
            name="referralCode"
            placeholder="Enter referral code for fee discount"
            value={formData.referralCode}
            onChange={handleChange}
          />
        </div>

        <div className="rounded-lg border p-4 space-y-4">
          <div className="flex items-start gap-3">
            <Checkbox
              id="fairLaunch"
              checked={formData.fairLaunch}
              onCheckedChange={(checked) => setFormData((previous) => ({ ...previous, fairLaunch: checked === true }))}
            />
            <div>
              <Label htmlFor="fairLaunch" className="cursor-pointer">Enable fair launch</Label>
              <p className="text-sm text-muted-foreground mt-1">Lock the full token supply in a launch pool and distribute tokens proportionally to MATIC participants.</p>
              <p className="text-xs text-muted-foreground mt-2">
                {formData.fairLaunch
                  ? "You will confirm up to 4 transactions: deploy the token, approve the pool, create the pool, and deposit the tokens."
                  : "Without Fair Launch, token creation normally requires 1 transaction, or 2 if you use a referral code."}
              </p>
            </div>
          </div>
          {formData.fairLaunch && (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="space-y-2">
                <div className="flex items-center gap-1"><Label htmlFor="fairLaunchDuration">Launch duration (seconds)</Label><TooltipProvider><Tooltip><TooltipTrigger asChild><Info className="h-4 w-4 text-muted-foreground cursor-help" /></TooltipTrigger><TooltipContent><p>How long users can commit MATIC. 86,400 seconds equals 24 hours. The launch can also finalize early if the MATIC target is reached.</p></TooltipContent></Tooltip></TooltipProvider></div>
                <Input id="fairLaunchDuration" name="fairLaunchDuration" type="number" min="3600" value={formData.fairLaunchDuration} onChange={handleChange} />
                <p className="text-xs text-muted-foreground">{formatDurationHours(formData.fairLaunchDuration)}</p>
              </div>
              <div className="space-y-2">
                <div className="flex items-center gap-1"><Label htmlFor="fairLaunchTarget">MATIC target</Label><TooltipProvider><Tooltip><TooltipTrigger asChild><Info className="h-4 w-4 text-muted-foreground cursor-help" /></TooltipTrigger><TooltipContent><p>The total MATIC commitment threshold for early finalization. For example, 20 means the launch can finalize once commitments reach 20 MATIC.</p></TooltipContent></Tooltip></TooltipProvider></div>
                <Input id="fairLaunchTarget" name="fairLaunchTarget" type="number" min="0.01" step="any" value={formData.fairLaunchTarget} onChange={handleChange} />
              </div>
            </div>
          )}
        </div>
        
        <FormButtons 
          isDeploying={isDeploying} 
          isFormValid={isNameValid && !!formData.symbol && !!formData.initialSupply}
        />
      </form>
    </Card>
  );
}
